import { Alert, Card, Grid, Slide, Snackbar, SnackbarCloseReason } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import useCurrentUser from 'src/hooks/use-current-user';
import {
  getAllCodesAction,
  getAllICDAction,
  getAllSubmittedDocument,
  getAllTemplates,
  resetEditSigndocAction,
  resetSendMailToPhysician,
  resetStartOfCareDate,
  resetUndoDocumentSignAction,
  resetreAssignTherapistAction,
} from 'src/redux/actions/documentationAction';
import { switchTherapistDisciplineAction } from 'src/redux/actions/patientAction';
import CompletedDocument from './completed-document-list';
import EocDetailModal from './eoc-detail-modal';
import SignaturePendingList from './signature-pending-list';
import ViewDocumentList from './view-documents-list';

const tabs = [
  {
    label: 'PT',
    value: 'PT',
  },
  {
    label: 'OT',
    value: 'OT',
  },
  {
    label: 'SLP',
    value: 'SLP',
  },
];

const Documentation = ({
  openPrintMode,
  documentationDoc,
  setDocumentationDoc,
  selectedDate,
  setIsFilterApply,
  handleCLearAllDoc,
}: any) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const switchTabs = (e: any, value: string) => {
    abortControllerCPTRef?.current?.abort();
    abortControllerIcdCodesRef?.current?.abort();
    abortControllerGetAllTemplateRef?.current?.abort();
    abortControllerScheduledDocumentRef?.current?.abort();
    abortControllerGetAllSubmissionRef?.current?.abort();
    openPrintMode && handleCLearAllDoc();
    dispatch(resetStartOfCareDate());
    dispatch(switchTherapistDisciplineAction(value));
  };
  const callSubmitEventReducer = useSelector((state: any) => state.callSubmitEventReducer);
  const [providerId, discipline] = useCurrentUser(['providerId', 'discipline']);
  const getUndoSignDocResponse = useSelector((state: any) => state.editSignDocumentReducer);
  const undoDocumentSignResponse = useSelector((state: any) => state.undoDocumentSignReducer);
  const activeTab = useSelector((state: any) => state.switchDisciplineReducer);
  const sendMailPhysicianResponse = useSelector((state: any) => state.sendMailToPhysicianReducer);
  const [getCPTcodes, setCPTcodes] = useState([]);
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const getAllCodesResponse = useSelector((state: any) => state.getAllCodeReducers);
  const reAssigneResponse = useSelector((state: any) => state.reAssigntherapistReducer);
  const abortControllerGetAllSubmissionRef = useRef<any>();
  const abortControllerGetAllTemplateRef = useRef<any>();
  const abortControllerIcdCodesRef = useRef<any>();
  const abortControllerCPTRef = useRef<any>();
  const abortControllerScheduledDocumentRef = useRef<any>();
  const getAllSignaturePending = useSelector((state: any) => state.getAllSubmittedDocumentReducer);
  const getAllCompletedDoc = useSelector((state: any) => state.getAllCompetedDocReducer);

  useEffect(() => {
    if (!activeTab || !patientData) return;
    abortControllerCPTRef.current = new AbortController();
    abortControllerIcdCodesRef.current = new AbortController();

    dispatch(
      getAllCodesAction(
        {
          discipline: activeTab,
          type: 'cpt',
          signal: abortControllerCPTRef.current.signal,
          eocUuid: location?.state?.episodeOfCareId,
        },
        patientData?.uuid
      )
    );
    dispatch(
      getAllICDAction(
        {
          discipline: activeTab,
          type: 'icd',
          signal: abortControllerIcdCodesRef.current.signal,
          eocUuid: location?.state?.episodeOfCareId,
        },
        patientData?.uuid
      )
    );
  }, [activeTab, patientData]);

  useEffect(() => {
    if (!activeTab) return;
    abortControllerGetAllTemplateRef.current = new AbortController();

    dispatch(
      getAllTemplates({
        type: activeTab,
        episodeOfCareId: location?.state?.episodeOfCareId,
        signal: abortControllerGetAllTemplateRef.current.signal,
      })
    );
  }, [activeTab]);

  useEffect(() => {
    if (!getAllCodesResponse.areGettingCodes && getAllCodesResponse.response === null) return;
    setCPTcodes(getAllCodesResponse.response?.data?.cptCodes);
  }, [getAllCodesResponse]);

  const callGetAllSubmittedDocument = () => {
    if (!activeTab) return;
    abortControllerGetAllSubmissionRef.current = new AbortController();

    dispatch(
      getAllSubmittedDocument({
        therapistUuid: providerId,
        eocId: location?.state?.episodeOfCareId,
        signal: abortControllerGetAllSubmissionRef.current.signal,
        disciplineChange: true,
        params: {
          type: activeTab,
          filter: 'PENDING',
          sortDirection: 'desc',
          sortBy: 'dateOfService',
          page: 0,
          pageSize: 10,
        },
      })
    );
  };

  useEffect(() => {
    if (openPrintMode && selectedDate?.startDate && selectedDate?.endDate) {
      abortControllerGetAllSubmissionRef.current = new AbortController();
      dispatch(
        getAllSubmittedDocument({
          therapistUuid: providerId,
          eocId: location?.state?.episodeOfCareId,
          signal: abortControllerGetAllSubmissionRef.current.signal,
          disciplineChange: true,
          params: {
            type: activeTab,
            filter: 'PENDING',
            startDate: moment(selectedDate?.startDate).format('YYYY-MM-DD'),
            endDate: moment(selectedDate?.endDate).format('YYYY-MM-DD'),
          },
        })
      );
    }
  }, [openPrintMode, selectedDate]);

  useEffect(() => {
    callGetAllSubmittedDocument();
  }, [callSubmitEventReducer]);

  useEffect(() => {
    if (!activeTab) return;
    callGetAllSubmittedDocument();
  }, [activeTab]);

  const [open, setOpen] = useState(false);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  useEffect(() => {
    if (!sendMailPhysicianResponse.sendMail && sendMailPhysicianResponse.response === null) return;
    callGetAllSubmittedDocument();
    setOpen(true);
    dispatch(resetSendMailToPhysician());
  }, [sendMailPhysicianResponse]);

  useEffect(() => {
    if (!getUndoSignDocResponse.isDocEdited && getUndoSignDocResponse.response === null) return;
    callGetAllSubmittedDocument();
    dispatch(resetEditSigndocAction());
  }, [getUndoSignDocResponse]);

  useEffect(() => {
    if (!undoDocumentSignResponse.isSuccess && undoDocumentSignResponse.response === null) return;
    callGetAllSubmittedDocument();
    dispatch(resetUndoDocumentSignAction());
  }, [undoDocumentSignResponse]);

  useEffect(() => {
    if (activeTab !== '') return;
    if (discipline) {
      dispatch(switchTherapistDisciplineAction(discipline));
    }
  }, [discipline]);

  useEffect(() => {
    if (!reAssigneResponse.isReAssign && reAssigneResponse.response === null) return;
    callGetAllSubmittedDocument();
    dispatch(resetreAssignTherapistAction());
  }, [reAssigneResponse]);

  const SlideTransition = useCallback((props: any) => {
    return (
      <Slide
        {...props}
        direction="left"
      />
    );
  }, []);

  return (
    <Box>
      <Grid
        display={'flex'}
        alignItems={'center'}
        marginBottom={1}
      >
        <Typography variant="h5">Plan Of Care & Documentation</Typography>
        <Tabs
          indicatorColor="primary"
          textColor="primary"
          value={activeTab}
          sx={{ px: 3 }}
          onChange={(e, value) => switchTabs(e, value)}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.value}
              label={tab.label}
              value={tab.value}
            />
          ))}
        </Tabs>
      </Grid>
      <Card>
        <EocDetailModal
          activeTab={activeTab}
          getCPTcodes={getCPTcodes}
        />
        <ViewDocumentList
          activeTab={activeTab}
          abortControllerScheduledDocumentRef={abortControllerScheduledDocumentRef}
          openPrintMode={openPrintMode}
        />
        <SignaturePendingList
          activeTab={activeTab}
          requireDocuments={getAllSignaturePending?.response?.content}
          openPrintMode={openPrintMode}
          documentationDoc={documentationDoc}
          setDocumentationDoc={setDocumentationDoc}
          setIsFilterApply={setIsFilterApply}
          abortController={abortControllerGetAllSubmissionRef?.current?.signal}
        />
        <CompletedDocument
          activeTab={activeTab}
          completedDocuments={getAllCompletedDoc?.response?.content}
          abortController={abortControllerGetAllSubmissionRef?.current?.signal}
          openPrintMode={openPrintMode}
          documentationDoc={documentationDoc}
          setDocumentationDoc={setDocumentationDoc}
          setIsFilterApply={setIsFilterApply}
        />
      </Card>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={open}
        autoHideDuration={2200}
        onClose={handleClose}
        TransitionComponent={SlideTransition}
      >
        <Alert
          onClose={handleClose}
          severity="success"
          variant="standard"
        >
          Signature request sent to physician successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Documentation;
